import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'
import { PdfTester } from './components/dev/PdfTester.tsx'
import { ShareView } from './components/share/ShareView.tsx'
import { Welcome } from './components/Welcome.tsx'
import { initAnalytics } from './lib/analytics.ts'

// Sentry: only initialize if DSN is provided at build time.
// Locally (no VITE_SENTRY_DSN set), this is a no-op and nothing ships to Sentry.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,             // 'production' on Cloudflare Pages, 'development' locally
    sendDefaultPii: false,                         // don't auto-capture IPs/headers; set explicit user via Sentry.setUser() after auth
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),                  // defaults mask all text + block media — safe for medical content
    ],
    // Tracing
    tracesSampleRate: 1.0,                         // 100% — fine at MVP scale; lower if quota becomes a concern
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/pharmatext-backend-.*\.run\.app/,  // covers any Cloud Run revision URL
      /^https:\/\/api\.pharmatext\.ai/,              // future custom domain (no-op until DNS set up)
    ],
    // Session Replay — record only sessions that hit errors (preserves free-tier quota)
    replaysSessionSampleRate: 0.0,                 // don't record clean sessions
    replaysOnErrorSampleRate: 1.0,                 // always record when an error occurs
  })
}

// PostHog: only initializes when VITE_POSTHOG_KEY is set (no-op otherwise).
initAnalytics()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/dev/pdf-tester" element={<PdfTester />} />
        {/* /share/:token is public — must sit OUTSIDE the App tree so the
            auto-redirect-to-WorkOS gate in App.tsx never runs for share
            recipients. */}
        <Route path="/share/:token" element={<ShareView />} />
        {/* /welcome is the Stripe Checkout success landing ("check your email").
            Public + pay-first → must sit OUTSIDE the App tree so App's
            auto-redirect-to-WorkOS gate never runs for the unauthenticated buyer. */}
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/*" element={<App />} />
      </Routes>
    </BrowserRouter>
  </StrictMode>,
)
